import { useEffect, useState } from 'react';
import { formatDate, formatDateTime } from '@/utils/format';
import { useModalBackHandler } from '@/hooks/useModalBackHandler';
import { WARNING_LEVELS, WARNING_TAG_CONFIG } from '@/utils/logParser';
import { getFileDownloadUrl } from '@/api/file';
import type { Diary, Field, Season, EditHistory } from '@/types';

interface LogDetailModalProps {
  log: Diary;
  field?: Field;
  season?: Season;
  histories: EditHistory[];
  onClose: () => void;
}

export function LogDetailModal({ log, field, season, histories, onClose }: LogDetailModalProps) {
  useModalBackHandler(true, onClose);
  const wl = WARNING_LEVELS[log.warningLevel || 'none'];
  const tags = log.warningTags || [];
  const allImages = [...(log.hinhAnhList || []), ...(log.hinhAnh ? [log.hinhAnh] : [])];
  const materialSuggestions = log.materialSuggestions || [];
  const attachments = log.attachments || [];

  const [attachmentUrls, setAttachmentUrls] = useState<Record<string, string>>({});
  const [attachmentErrors, setAttachmentErrors] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let cancelled = false;
    attachments.forEach((a) => {
      if (attachmentUrls[a.fileId] || attachmentErrors[a.fileId]) return;
      getFileDownloadUrl(a.fileId)
        .then((url) => {
          if (!cancelled) setAttachmentUrls((prev) => ({ ...prev, [a.fileId]: url }));
        })
        .catch(() => {
          if (!cancelled) setAttachmentErrors((prev) => ({ ...prev, [a.fileId]: true }));
        });
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [log.id]);

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/40 animate-modal-backdrop" onClick={onClose} />
      <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-lg max-h-[100dvh] sm:max-h-[90vh] overflow-y-auto animate-modal-slide-up">
        <div className="sticky top-0 bg-white border-b border-slate-100 px-4 py-3 flex items-center justify-between z-10">
          <h3 className="text-base font-semibold text-slate-900">Chi tiết nhật ký</h3>
          <button type="button" onClick={onClose} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600">
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="p-4 space-y-5">
          {/* Basic info grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <p className="text-xs font-medium text-slate-500 mb-1">Ngày ghi nhận</p>
              <p className="text-sm font-semibold text-slate-900">{formatDate(log.ngay)}</p>
            </div>
            {field && (
              <div>
                <p className="text-xs font-medium text-slate-500 mb-1">Thửa đất</p>
                <p className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center text-slate-400"><i className="ri-map-pin-line" /></span>
                  {field.maThuaDat} - {field.name}
                </p>
              </div>
            )}
            {season && (
              <div>
                <p className="text-xs font-medium text-slate-500 mb-1">Mùa vụ</p>
                <p className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center text-slate-400"><i className="ri-seedling-line" /></span>
                  {season.maMuaVu} - {season.name}
                </p>
              </div>
            )}
            {log.editCount !== undefined && log.editCount > 0 && (
              <div>
                <p className="text-xs font-medium text-slate-500 mb-1">Số lần sửa</p>
                <p className="text-sm text-slate-600">{log.editCount} lần{log.editedAt ? ` - lần cuối ${formatDateTime(log.editedAt)}` : ''}</p>
              </div>
            )}
          </div>

          {/* Warning badge */}
          {(wl.label || tags.length > 0) && (
            <div className={`rounded-xl p-3 ${wl.bg || 'bg-slate-50'} border ${wl.border || 'border-slate-100'}`}>
              <p className="text-xs font-semibold text-slate-700 mb-1.5">Cảnh báo</p>
              <div className="flex gap-1.5 flex-wrap">
                {wl.label && (
                  <span className={`inline-flex items-center gap-1 text-xs font-medium rounded-full px-2.5 py-1 ${wl.bg} ${wl.textColor}`}>
                    <span className={`w-3.5 h-3.5 flex items-center justify-center ${wl.iconColor}`}><i className={wl.icon} /></span>
                    {wl.label}
                  </span>
                )}
                {tags.map((tag) => {
                  const cfg = WARNING_TAG_CONFIG[tag];
                  if (!cfg) return null;
                  const levelBg = cfg.level === 'danger' ? 'bg-red-100 text-red-700 border-red-200' :
                    cfg.level === 'check' ? 'bg-orange-100 text-orange-700 border-orange-200' :
                    'bg-amber-100 text-amber-700 border-amber-200';
                  return (
                    <span key={tag} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${levelBg}`}>
                      <span className="w-3 h-3 flex items-center justify-center"><i className={`${cfg.icon} text-xs`} /></span>
                      {cfg.label}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Content */}
          <div>
            <p className="text-xs font-medium text-slate-500 mb-1.5">Nội dung nhật ký</p>
            <div className="bg-white border border-slate-100 rounded-xl p-3">
              <p className="text-sm font-medium text-slate-900 mb-1">{log.hoatDong}</p>
              <p className="text-sm text-slate-700 whitespace-pre-line">{log.chiTiet}</p>
            </div>
          </div>

          {/* Materials note */}
          {log.materialsNote && (
            <div>
              <p className="text-xs font-medium text-slate-500 mb-1.5">Ghi chú vật tư sử dụng</p>
              <div className="bg-agrida-50 border border-agrida-100 rounded-xl p-3">
                <p className="text-sm text-agrida-800">{log.materialsNote}</p>
              </div>
            </div>
          )}

          {/* Structured materials/cost breakdown */}
          {log.materials && log.materials.length > 0 && (
            <div>
              <p className="text-xs font-medium text-slate-500 mb-1.5">Vật tư & chi phí</p>
              <div className="bg-white border border-slate-100 rounded-xl p-3">
                <table className="w-full text-sm">
                  <tbody>
                    {log.materials.map((m, idx) => (
                      <tr key={idx} className="border-b border-slate-50 last:border-0">
                        <td className="py-1 text-slate-700">{m.name}</td>
                        <td className="py-1 text-slate-500 text-right whitespace-nowrap">{m.quantity} {m.unit}</td>
                        <td className="py-1 text-slate-700 text-right whitespace-nowrap">{m.amountVnd ? `${m.amountVnd.toLocaleString('vi-VN')} đ` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {log.materials.some((m) => m.amountVnd) && (
                  <p className="mt-1.5 text-xs font-semibold text-slate-700 text-right">
                    Tổng: {log.materials.reduce((sum, m) => sum + (m.amountVnd ?? 0), 0).toLocaleString('vi-VN')} đ
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Labor hours + issue */}
          {(log.laborHours || log.issueNote) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {log.laborHours && (
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-1">Giờ công</p>
                  <p className="text-sm font-semibold text-slate-900">{log.laborHours} giờ</p>
                </div>
              )}
              {log.issueNote && (
                <div className="sm:col-span-2">
                  <div className="flex items-center gap-1.5 mb-1">
                    <p className="text-xs font-medium text-slate-500">Vấn đề gặp phải</p>
                    {log.issueSeverity && (
                      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                        log.issueSeverity === 'HIGH' ? 'bg-red-100 text-red-700' :
                        log.issueSeverity === 'MEDIUM' ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {log.issueSeverity === 'HIGH' ? 'Nghiêm trọng' : log.issueSeverity === 'MEDIUM' ? 'Trung bình' : 'Nhẹ'}
                      </span>
                    )}
                  </div>
                  <div className="bg-amber-50 border border-amber-100 rounded-xl p-3">
                    <p className="text-sm text-amber-800">{log.issueNote}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Material suggestions from AI */}
          {materialSuggestions.length > 0 && (
            <div>
              <p className="text-xs font-medium text-slate-500 mb-1.5">Gợi ý vật tư (AI)</p>
              <div className="space-y-1.5">
                {materialSuggestions.map((s, idx) => (
                  <div key={idx} className="bg-slate-50 border border-slate-100 rounded-xl p-2.5 flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-800">{s.tenVatTu}</span>
                        {s.nhomVatTu && (
                          <span className="text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">{s.nhomVatTu}</span>
                        )}
                        <span className="text-xs text-slate-500">{s.soLuong} {s.donVi}</span>
                      </div>
                    </div>
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${s.applied ? 'bg-agrida-100 text-agrida-700' : 'bg-amber-100 text-amber-700'}`}>
                      {s.applied ? 'Đã cập nhật' : 'Chưa áp dụng'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Ghi chú khác */}
          {log.ghiChuKhac && (
            <div>
              <p className="text-xs font-medium text-slate-500 mb-1.5">Ghi chú khác</p>
              <div className="bg-slate-50 border border-slate-100 rounded-xl p-3">
                <p className="text-sm text-slate-700">{log.ghiChuKhac}</p>
              </div>
            </div>
          )}

          {/* HTX notes */}
          {log.ghiChuHTX && (
            <div>
              <p className="text-xs font-medium text-slate-500 mb-1.5">Ghi chú HTX</p>
              <div className="bg-sky-50 border border-sky-100 rounded-xl p-3 flex items-start gap-2">
                <span className="w-4 h-4 flex items-center justify-center text-sky-600 shrink-0 mt-0.5"><i className="ri-chat-1-line" /></span>
                <p className="text-sm text-sky-800">{log.ghiChuHTX}</p>
              </div>
            </div>
          )}

          {/* Voice script */}
          {log.ghiAmScript && (
            <div>
              <p className="text-xs font-medium text-slate-500 mb-1.5">Ghi âm</p>
              <div className="bg-agrida-50 border border-agrida-100 rounded-xl p-3 flex items-start gap-2">
                <span className="w-4 h-4 flex items-center justify-center text-agrida-600 shrink-0 mt-0.5"><i className="ri-mic-fill" /></span>
                <p className="text-sm text-agrida-800">"{log.ghiAmScript}"</p>
              </div>
            </div>
          )}

          {/* Real captured/uploaded attachments (photo/video/audio) */}
          {attachments.length > 0 && (
            <div>
              <p className="text-xs font-medium text-slate-500 mb-2">Đính kèm (ảnh / video / ghi âm)</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {attachments.map((a) => {
                  const url = attachmentUrls[a.fileId];
                  const failed = attachmentErrors[a.fileId];
                  if (a.type === 'audio') {
                    return (
                      <div key={a.fileId} className="col-span-2 sm:col-span-3 bg-slate-50 border border-slate-100 rounded-xl p-2.5">
                        {url ? <audio src={url} controls className="w-full" /> : <p className="text-xs text-slate-400">{failed ? 'Không tải được ghi âm' : 'Đang tải ghi âm...'}</p>}
                        {a.caption && <p className="text-xs text-slate-500 mt-1">{a.caption}</p>}
                      </div>
                    );
                  }
                  return (
                    <div key={a.fileId} className="aspect-square rounded-xl overflow-hidden bg-slate-100 flex items-center justify-center">
                      {!url && !failed && <span className="text-xs text-slate-400">Đang tải...</span>}
                      {failed && <span className="text-xs text-red-400 text-center px-1">Không tải được</span>}
                      {url && a.type === 'video' && <video src={url} controls className="w-full h-full object-cover" />}
                      {url && a.type === 'image' && <img src={url} alt={a.caption ?? 'ảnh đính kèm'} className="w-full h-full object-cover" />}
                      {url && a.type === 'doc' && (
                        <a href={url} target="_blank" rel="noreferrer" className="flex flex-col items-center gap-1 text-slate-500 hover:text-agrida-700">
                          <i className="ri-file-text-line text-2xl" />
                          <span className="text-[10px] px-1 truncate max-w-full">{a.caption ?? 'Xem tài liệu'}</span>
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Legacy mock photo/video fields — kept only so any leftover
              pre-migration diary entries still render something. */}
          {(allImages.length > 0 || log.videoUrl) && (
            <div>
              <p className="text-xs font-medium text-slate-500 mb-2">Ảnh / Video đính kèm (cũ)</p>
              {allImages.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
                  {allImages.map((url, idx) => (
                    <div key={idx} className="aspect-square rounded-xl overflow-hidden bg-slate-100">
                      <img src={url} alt={`img-${idx}`} className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              )}
              {log.videoUrl && (
                <video src={log.videoUrl} controls className="w-full rounded-xl max-h-[240px]" />
              )}
            </div>
          )}

          {/* Deleted info */}
          {log.isDeleted && log.deletedReason && (
            <div className="bg-red-50 border border-red-100 rounded-xl p-3">
              <p className="text-xs font-semibold text-red-700 mb-1">Đã xóa</p>
              <p className="text-xs text-red-600">Lý do: {log.deletedReason}</p>
              {log.deletedAt && <p className="text-xs text-red-500 mt-0.5">Ngày xóa: {formatDateTime(log.deletedAt)}</p>}
            </div>
          )}

          {/* Edit history */}
          {histories.length > 0 && (
            <div className="border-t border-slate-100 pt-4">
              <p className="text-xs font-medium text-slate-500 mb-2">Lịch sử chỉnh sửa ({histories.length})</p>
              <div className="space-y-2">
                {histories.map((h) => (
                  <div key={h.id} className="text-xs text-slate-600 bg-slate-50 rounded-xl p-3">
                    <p className="font-medium">{formatDateTime(h.ngaySua)} - {h.nguoiSua}</p>
                    <p className="mt-1">Thay đổi <span className="font-medium">{h.truong}</span>:</p>
                    <p className="text-slate-400 line-through mt-0.5 break-words">{h.giaTriCu || '(trống)'}</p>
                    <p className="text-agrida-700 mt-0.5 break-words">{h.giaTriMoi || '(trống)'}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-100 px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="w-full px-4 py-2.5 rounded-md text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors whitespace-nowrap min-h-[44px]"
          >
            Đóng
          </button>
        </div>
      </div>
    </div>
  );
}